import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ChevronDown, X } from "lucide-react";
import { carrierEmails } from "@/hooks/useRfpEmail";

// Multi-select carrier dropdown for the RFP modal (preferred carriers listed first).
export const CarrierPicker = ({ emailCarriers, selectedIds, onToggle, selectedCount, recommendedIds, onSelectPreferred, onSelectAll, onClear }) => {
  const preferred = emailCarriers.filter((c) => c.preferred);
  const others = emailCarriers.filter((c) => !c.preferred);
  const emailSummary = (c) => {
    const list = carrierEmails(c);
    if (!list.length) return "";
    return list.length > 1 ? `${list[0]} +${list.length - 1} more contact${list.length > 2 ? "s" : ""}` : list[0];
  };
  const row = (c) => (
    <label
      key={c.id}
      className="flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-stone-50 cursor-pointer"
      data-testid={`rfp-carrier-option-${c.id}`}
    >
      <Checkbox
        checked={selectedIds.includes(c.id)}
        onCheckedChange={() => onToggle(c.id)}
        className="mt-0.5"
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-sm text-[#1C1917]">
          <span className="truncate">
            {c.preferred && <span className="text-[#F97316]">★ </span>}
            {c.name}
          </span>
          {recommendedIds.includes(c.id) && (
            <span className="shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase bg-[#FFF7ED] text-[#EA580C] border border-[#F97316]/30 rounded px-1 py-0.5">
              <Sparkles className="h-2.5 w-2.5" /> AI pick
            </span>
          )}
        </span>
        <span className="block text-xs text-[#57534E] truncate">{emailSummary(c)}</span>
      </span>
    </label>
  );
  return (
    <Popover modal={true}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="mt-1 w-full justify-between font-normal" data-testid="rfp-carrier-select-trigger">
          {selectedCount ? `${selectedCount} carrier(s) selected` : "Select carriers…"}
          <ChevronDown className="h-4 w-4 ml-2 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div
          className="p-2 overflow-y-auto"
          style={{ maxHeight: 280, WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", touchAction: "pan-y" }}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          data-testid="rfp-carrier-list"
        >
          {emailCarriers.length === 0 ? (
            <p className="text-xs text-[#57534E] p-2">
              No carriers have an email yet. Add emails in the Carrier Directory.
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between px-2 py-1.5 border-b border-border mb-1">
                <button
                  onClick={onSelectAll}
                  className="text-[11px] font-bold text-[#EA580C] hover:underline"
                  data-testid="rfp-select-all-carriers"
                >
                  Select all ({emailCarriers.length})
                </button>
                <button
                  onClick={onClear}
                  disabled={!selectedCount}
                  className="text-[11px] font-bold text-[#57534E] hover:underline disabled:opacity-40 disabled:no-underline"
                  data-testid="rfp-clear-selection"
                >
                  Clear selection
                </button>
              </div>
              {preferred.length > 0 && (
                <>
                  <div className="flex items-center justify-between px-2 pt-1 pb-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-[#EA580C]">★ Preferred</span>
                    <button
                      onClick={onSelectPreferred}
                      className="text-[10px] font-bold text-[#EA580C] hover:underline"
                      data-testid="rfp-select-all-preferred"
                    >
                      Select preferred
                    </button>
                  </div>
                  {preferred.map(row)}
                </>
              )}
              {others.length > 0 && preferred.length > 0 && (
                <div className="px-2 pt-2 pb-0.5 text-[10px] font-bold uppercase tracking-wide text-[#57534E]">
                  All carriers
                </div>
              )}
              {others.map(row)}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Removable chips for the currently selected carriers.
export const CarrierChips = ({ selectedCarriers, onToggle }) =>
  selectedCarriers.map((c) => (
    <Badge
      key={c.id}
      variant="secondary"
      className="bg-[#FFF7ED] text-[#EA580C] border border-[#F97316]/30 gap-1"
      data-testid={`rfp-carrier-chip-${c.id}`}
    >
      {c.name}
      <button onClick={() => onToggle(c.id)} className="hover:text-[#1C1917]">
        <X className="h-3 w-3" />
      </button>
    </Badge>
  ));
